import { useEffect, useMemo, useState } from 'react'
import { MetricPeriod } from '@/lib/utils'

export interface AnalyticsSelectedRange {
  mode: 'period' | 'week' | 'last_3_days'
  start: string
  end: string
  label: string
  rangeDays: number
  weekNumber: number | null
}

interface UseAnalyticsTimeFilterOptions {
  includeLastThreeDaysOption?: boolean
  isCurrentPeriod?: boolean
}

function formatIsoDate(date: Date): string {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

export function useAnalyticsTimeFilter(
  currentPeriod: MetricPeriod | null,
  options: UseAnalyticsTimeFilterOptions = {}
) {
  const { includeLastThreeDaysOption = false, isCurrentPeriod = true } = options
  const [selectedTimeFilter, setSelectedTimeFilter] = useState<string>('current_period')

  const availableWeekOptions = useMemo(() => {
    if (!currentPeriod) return [] as Array<{ value: string; label: string }>

    const periodStart = new Date(currentPeriod.period_start)
    const periodEnd = new Date(currentPeriod.period_end)
    const diffTime = periodEnd.getTime() - periodStart.getTime()
    const totalDays = Math.max(1, Math.floor(diffTime / (1000 * 60 * 60 * 24)) + 1)
    const weekCount = Math.ceil(totalDays / 7)

    return Array.from({ length: weekCount }, (_, index) => ({
      value: `week_${index + 1}`,
      label: `Неделя ${index + 1}`
    }))
  }, [currentPeriod])

  const timeFilterOptions = useMemo(
    () => [
      { value: 'current_period', label: 'Период целиком' },
      ...(includeLastThreeDaysOption && isCurrentPeriod ? [{ value: 'last_3_days', label: '3 последних дня' }] : []),
      ...availableWeekOptions
    ],
    [availableWeekOptions, includeLastThreeDaysOption, isCurrentPeriod]
  )

  const selectedRange = useMemo<AnalyticsSelectedRange | null>(() => {
    if (!currentPeriod) return null

    if (includeLastThreeDaysOption && isCurrentPeriod && selectedTimeFilter === 'last_3_days') {
      const yesterday = new Date()
      yesterday.setHours(0, 0, 0, 0)
      yesterday.setDate(yesterday.getDate() - 1)

      const actualEnd = new Date(yesterday)
      const actualStart = new Date(yesterday)
      actualStart.setDate(actualStart.getDate() - 2)

      return {
        mode: 'last_3_days',
        start: formatIsoDate(actualStart),
        end: formatIsoDate(actualEnd),
        label: '3 последних дня',
        rangeDays: 3,
        weekNumber: null
      }
    }

    if (selectedTimeFilter === 'current_period') {
      return {
        mode: 'period',
        start: currentPeriod.period_start,
        end: currentPeriod.period_end,
        label: 'Период целиком',
        rangeDays: Math.max(1, currentPeriod.period_len_days || 1),
        weekNumber: null
      }
    }

    if (selectedTimeFilter.startsWith('week_')) {
      const weekNum = Number.parseInt(selectedTimeFilter.split('_')[1], 10)
      if (!Number.isNaN(weekNum) && weekNum > 0) {
        const periodStart = new Date(currentPeriod.period_start)
        const periodEnd = new Date(currentPeriod.period_end)
        const weekStart = new Date(periodStart)
        weekStart.setDate(periodStart.getDate() + (weekNum - 1) * 7)
        const weekEnd = new Date(weekStart)
        weekEnd.setDate(weekStart.getDate() + 6)
        const actualEnd = weekEnd > periodEnd ? periodEnd : weekEnd
        if (weekStart <= periodEnd) {
          const start = `${weekStart.getFullYear()}-${String(weekStart.getMonth() + 1).padStart(2, '0')}-${String(weekStart.getDate()).padStart(2, '0')}`
          const end = `${actualEnd.getFullYear()}-${String(actualEnd.getMonth() + 1).padStart(2, '0')}-${String(actualEnd.getDate()).padStart(2, '0')}`
          const rangeDays = Math.floor((actualEnd.getTime() - weekStart.getTime()) / (1000 * 60 * 60 * 24)) + 1
          return {
            mode: 'week',
            start,
            end,
            label: `Неделя ${weekNum}`,
            rangeDays,
            weekNumber: weekNum
          }
        }
      }
    }

    return {
      mode: 'period',
      start: currentPeriod.period_start,
      end: currentPeriod.period_end,
      label: 'Период целиком',
      rangeDays: Math.max(1, currentPeriod.period_len_days || 1),
      weekNumber: null
    }
  }, [includeLastThreeDaysOption, isCurrentPeriod, selectedTimeFilter, currentPeriod])

  useEffect(() => {
    if (selectedTimeFilter === 'current_period') return
    const hasOption = timeFilterOptions.some((option) => option.value === selectedTimeFilter)
    if (!hasOption) {
      setSelectedTimeFilter('current_period')
    }
  }, [selectedTimeFilter, timeFilterOptions])

  return {
    selectedTimeFilter,
    setSelectedTimeFilter,
    availableWeekOptions,
    timeFilterOptions,
    selectedRange
  }
}
